import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { BookOpen, Plus, Pencil, Trash2, Target, TrendingUp, AlertTriangle, Calendar, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_TYPES = [
  { value: 'practice', label: 'Practice', color: 'bg-blue-100 text-blue-700' },
  { value: 'match', label: 'Match', color: 'bg-green-100 text-green-700' },
  { value: 'drill', label: 'Drill', color: 'bg-orange-100 text-orange-700' },
  { value: 'fitness', label: 'Fitness', color: 'bg-purple-100 text-purple-700' },
  { value: 'video_review', label: 'Video Review', color: 'bg-indigo-100 text-indigo-700' }
];

const getSessionBadge = (type) => {
  const st = SESSION_TYPES.find(s => s.value === type);
  return st || { label: type, color: 'bg-gray-100 text-gray-700' };
};

const DiaryPage = ({ isDark }) => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    session_type: 'practice',
    date: format(new Date(), 'yyyy-MM-dd'),
    what_went_well: '',
    what_to_improve: '',
    goals_next_session: '',
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
  }, [filterType]);

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('session_type', filterType);
      const response = await axios.get(`${API}/diary/?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch diary entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      session_type: 'practice',
      date: format(new Date(), 'yyyy-MM-dd'),
      what_went_well: '',
      what_to_improve: '',
      goals_next_session: '',
      notes: ''
    });
    setEditingEntry(null);
  };

  const handleSubmit = async () => {
    if (!form.what_went_well.trim() || !form.what_to_improve.trim()) {
      toast({ title: 'Please fill in required fields', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingEntry) {
        await axios.put(`${API}/diary/${editingEntry.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Entry updated' });
      } else {
        await axios.post(`${API}/diary/`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Entry saved', description: 'Keep learning and improving!' });
      }
      setShowForm(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast({ title: 'Failed to save entry', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setForm({
      session_type: entry.session_type,
      date: entry.date,
      what_went_well: entry.what_went_well,
      what_to_improve: entry.what_to_improve,
      goals_next_session: entry.goals_next_session || '',
      notes: entry.notes || ''
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/diary/${deleteConfirm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Entry deleted' });
      setDeleteConfirm(null);
      fetchEntries();
    } catch (error) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="diary-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
          <BookOpen className="h-5 w-5 text-blue-600" />
          Session Diary
        </h2>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-diary-entry-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> New Entry
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="diary-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sessions</SelectItem>
            {SESSION_TYPES.map(st => (
              <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Entries List */}
      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">No diary entries yet</p>
            <p className="text-sm text-gray-400 mt-1">Record what you learned after each session</p>
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              variant="outline"
              className="mt-4"
            >
              <Plus className="h-4 w-4 mr-1" /> Write your first entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => {
            const badge = getSessionBadge(entry.session_type);
            return (
              <Card key={entry.id} className="hover:shadow-md transition-shadow" data-testid={`diary-entry-${entry.id}`}>
                <CardContent className="p-4">
                  {/* Entry Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className={`${badge.color} text-[10px] font-medium`}>{badge.label}</Badge>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(entry.date + 'T00:00:00'), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(entry)} data-testid={`edit-diary-${entry.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteConfirm(entry.id)} data-testid={`delete-diary-${entry.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wide">What went well</p>
                        <p className="text-sm text-gray-700">{entry.what_went_well}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide">To improve</p>
                        <p className="text-sm text-gray-700">{entry.what_to_improve}</p>
                      </div>
                    </div>
                    {entry.goals_next_session && (
                      <div className="flex items-start gap-2">
                        <Target className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Goals for next session</p>
                          <p className="text-sm text-gray-700">{entry.goals_next_session}</p>
                        </div>
                      </div>
                    )}
                    {entry.notes && (
                      <p className="text-xs text-gray-500 mt-2 pl-6 italic">{entry.notes}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              {editingEntry ? 'Edit Entry' : 'New Session Entry'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Session Type + Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Session Type</Label>
                <Select value={form.session_type} onValueChange={(v) => setForm(prev => ({ ...prev, session_type: v }))}>
                  <SelectTrigger className="h-9 mt-1" data-testid="diary-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_TYPES.map(st => (
                      <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                  className="h-9 mt-1"
                  data-testid="diary-form-date"
                />
              </div>
            </div>

            {/* What went well */}
            <div>
              <Label className="text-xs font-medium flex items-center gap-1">
                <TrendingUp className="h-3.5 w-3.5 text-green-500" /> What went well *
              </Label>
              <Textarea
                placeholder="e.g. My net shots were tight today, footwork improved on the backhand side..."
                value={form.what_went_well}
                onChange={(e) => setForm(prev => ({ ...prev, what_went_well: e.target.value }))}
                rows={3}
                className="mt-1"
                data-testid="diary-form-well"
              />
            </div>

            {/* What to improve */}
            <div>
              <Label className="text-xs font-medium flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> What to improve *
              </Label>
              <Textarea
                placeholder="e.g. Still dropping my racket after smash, need to recover faster..."
                value={form.what_to_improve}
                onChange={(e) => setForm(prev => ({ ...prev, what_to_improve: e.target.value }))}
                rows={3}
                className="mt-1"
                data-testid="diary-form-improve"
              />
            </div>

            {/* Goals */}
            <div>
              <Label className="text-xs font-medium flex items-center gap-1">
                <Target className="h-3.5 w-3.5 text-blue-500" /> Goals for next session
              </Label>
              <Textarea
                placeholder="e.g. Focus on cross-court drops, practice 50 jump smashes..."
                value={form.goals_next_session}
                onChange={(e) => setForm(prev => ({ ...prev, goals_next_session: e.target.value }))}
                rows={2}
                className="mt-1"
                data-testid="diary-form-goals"
              />
            </div>

            {/* Extra notes */}
            <div>
              <Label className="text-xs font-medium">Additional Notes</Label>
              <Textarea
                placeholder="Any other thoughts, observations, or reminders..."
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="mt-1"
                data-testid="diary-form-notes"
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="diary-form-save-btn">
              {saving ? 'Saving...' : editingEntry ? 'Update Entry' : 'Save Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Entry
            </DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this diary entry?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-diary-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiaryPage;
